/**
 * Preferences Service
 * Handles fetching and updating team preferences (table columns, etc.)
 */

import axiosInstance from './api/axiosInstance';

class PreferencesService {
  /**
   * Get numeric team ID from various formats (team name, object, or numeric ID)
   * @param {string|number|object} teamId - Team ID in various formats
   * @returns {number|null} Numeric team ID or null if invalid
   */
  static getNumericTeamId(teamId) {
    if (!teamId) return null;

    // If it's already a number
    if (typeof teamId === 'number') {
      return teamId;
    }

    // If it's an object, extract ID
    if (typeof teamId === 'object' && teamId !== null) {
      return parseInt(teamId._id || teamId.id, 10);
    }

    // If it's a string, try to get from selectedTeamData first
    if (typeof teamId === 'string') {
      try {
        const teamDataStr = localStorage.getItem('selectedTeamData');
        if (teamDataStr) {
          const teamData = JSON.parse(teamDataStr);
          const numericId = parseInt(teamData._id || teamData.id, 10);
          if (!isNaN(numericId) && numericId) {
            return numericId;
          }
        }
      } catch (e) {
        // Fall through to try parsing as number
      }

      // Try parsing as number directly
      const numericId = parseInt(teamId, 10);
      if (!isNaN(numericId) && numericId) {
        return numericId;
      }
    }

    return null;
  }

  /**
   * Get events table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async getEventsTableColumns(teamId) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.get(
        `/team/${numericTeamId}/preferences/events-table-columns`
      );
      
      if (response.data.success) {
        return response.data.columns;
      }
      
      throw new Error('Failed to fetch events table columns');
    } catch (error) {
      console.error('Error fetching events table columns:', error);
      // Return default columns on error
      const { DEFAULT_COLUMNS } = require('../../features/conversion-events/config/eventColumns');
      return DEFAULT_COLUMNS;
    }
  }

  /**
   * Update events table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @param {string[]} enabled - Array of enabled column keys
   * @param {string[]} order - Array of column keys in display order
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async updateEventsTableColumns(teamId, enabled, order) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.put(
        `/team/${numericTeamId}/preferences/events-table-columns`,
        { enabled, order }
      );
      
      if (response.data.success) {
        return response.data.columns;
      }
      
      throw new Error(response.data.message || 'Failed to update events table columns');
    } catch (error) {
      console.error('Error updating events table columns:', error);
      throw error;
    }
  }

  /**
   * Get transactions table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async getTransactionsTableColumns(teamId) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.get(
        `/team/${numericTeamId}/preferences/transactions-table-columns`
      );

      if (response.data.success) {
        return response.data.columns;
      }

      throw new Error('Failed to fetch transactions table columns');
    } catch (error) {
      console.error('Error fetching transactions table columns:', error);
      const { DEFAULT_COLUMNS } = require('../../features/conversion-events/config/transactionColumns');
      return DEFAULT_COLUMNS;
    }
  }

  /**
   * Update transactions table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @param {string[]} enabled - Array of enabled column keys
   * @param {string[]} order - Array of column keys in display order
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async updateTransactionsTableColumns(teamId, enabled, order) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.put(
        `/team/${numericTeamId}/preferences/transactions-table-columns`,
        { enabled, order }
      );

      if (response.data.success) {
        return response.data.columns;
      }

      throw new Error(response.data.message || 'Failed to update transactions table columns');
    } catch (error) {
      console.error('Error updating transactions table columns:', error);
      throw error;
    }
  }

  /**
   * Get sessions table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async getSessionsTableColumns(teamId) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.get(
        `/team/${numericTeamId}/preferences/sessions-table-columns`
      );

      if (response.data.success) {
        return response.data.columns;
      }

      throw new Error('Failed to fetch sessions table columns');
    } catch (error) {
      console.error('Error fetching sessions table columns:', error);
      const { DEFAULT_COLUMNS } = require('../../features/conversion-events/config/sessionColumns');
      return DEFAULT_COLUMNS;
    }
  }

  /**
   * Update sessions table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @param {string[]} enabled - Array of enabled column keys
   * @param {string[]} order - Array of column keys in display order
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async updateSessionsTableColumns(teamId, enabled, order) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.put(
        `/team/${numericTeamId}/preferences/sessions-table-columns`,
        { enabled, order }
      );

      if (response.data.success) {
        return response.data.columns;
      }

      throw new Error(response.data.message || 'Failed to update sessions table columns');
    } catch (error) {
      console.error('Error updating sessions table columns:', error);
      throw error;
    }
  }

  /**
   * Get users table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async getUsersTableColumns(teamId) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.get(
        `/team/${numericTeamId}/preferences/users-table-columns`
      );

      if (response.data.success) {
        return response.data.columns;
      }

      throw new Error('Failed to fetch users table columns');
    } catch (error) {
      console.error('Error fetching users table columns:', error);
      const { DEFAULT_COLUMNS } = require('../../features/conversion-events/config/userColumns');
      return DEFAULT_COLUMNS;
    }
  }

  /**
   * Update users table column preferences for a team
   * @param {string|number|object} teamId - Team ID (can be name, object, or numeric ID)
   * @param {string[]} enabled - Array of enabled column keys
   * @param {string[]} order - Array of column keys in display order
   * @returns {Promise<{enabled: string[], order: string[]}>}
   */
  static async updateUsersTableColumns(teamId, enabled, order) {
    try {
      const numericTeamId = PreferencesService.getNumericTeamId(teamId);
      if (!numericTeamId) {
        throw new Error('Invalid team ID');
      }

      const response = await axiosInstance.put(
        `/team/${numericTeamId}/preferences/users-table-columns`,
        { enabled, order }
      );

      if (response.data.success) {
        return response.data.columns;
      }

      throw new Error(response.data.message || 'Failed to update users table columns');
    } catch (error) {
      console.error('Error updating users table columns:', error);
      throw error;
    }
  }
}

export default PreferencesService;
